import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";

import { DropArea } from "../../../Blocks/Composites/DragAndDrop/DropArea";
import { BigInfoIcon, Flex, List, Pagination, Search, Text } from "@theorgchartdev/storybook";
import { getLocalizedPaginationStrings } from "@/Utils/Utils";
import { StagingBranch } from "./StagingBranch";
import { StagingHeader } from "./StagingHeader";
import { useStagingArea } from "./useStagingArea";
import { appstr } from "@coreapp/utils/appstr";
import { useTopic } from "@/hooks/useTopic";

type DragPanel = {
  GetDragElement: () => { fParentPanel: { fOverParkingPanel: boolean } };
  DragComplete: () => void;
};

const StagingArea = () => {
  const manager = useStagingArea();
  const [[startIndex, endIndex], setPaginatedData] = useState<[number, number]>([0, 0]);
  const currentPageData = manager.branches.slice(startIndex, endIndex);
  const rootRef = useRef<any>(null);

  const branchKeys = manager.branches.map((obj: any) => obj.GetObjectID());
  const paginatedKeys = useMemo(() => {
    return branchKeys;
  }, [JSON.stringify(branchKeys)]);

  const options = currentPageData.map((obj: any) => ({
    padding: false,
    dataTestId: `staging-branch-${obj.SmartGetFieldValue("Name")}`,
    content: <StagingBranch branch={obj} manager={manager} />,
    value: obj.GetObjectID()
  }));

  const handleDragEnd = (panel: DragPanel) => {
    panel.GetDragElement().fParentPanel.fOverParkingPanel = true;
    panel.DragComplete();
  };

  const handleInputChange = (newQuery = "") => {
    manager.setSearching(newQuery.length > 0);
    manager.setSearchQuery(newQuery);
  };

  useEffect(() => {
    rootRef.current = manager.stagingState.root;
  }, [manager.stagingState.root]);

  useTopic("UndoChanges", () => {
    const currentRoot = rootRef.current;

    if (manager.objectInTree(currentRoot)) return;
    else manager.setLevelToTop();
  });

  return (
    <DropArea
      data-testid={"staging-area"}
      className="oc-staging-area--wrapper"
      onDragEnd={handleDragEnd}
    >
      <Flex className="sidebar-header-buttons" direction="column" gap="16px">
        <Text size="small">{appstr("Staging info")}</Text>
        <Search
          clearable
          inputProps={{
            id: "staging-search",
            style: { fontSize: "14px" },
            spellCheck: false
          }}
          size="small"
          disabled={manager.totalDisconnectedCount === 0}
          onInput={handleInputChange}
          onResetInput={() => handleInputChange()}
          placeholder={`${appstr("Search")} ${appstr("Staging")}`}
          timeout={200}
        />
      </Flex>
      {manager.totalDisconnectedCount === 0 ? (
        <BigInfoIcon
          color="brand"
          name="staging"
          message={appstr("No disconnected records found")}
        />
      ) : (
        <Flex
          className={cx(
            "oc-staging-area",
            "sidebar-header-buttons",
            "sidebar-content-item--divider"
          )}
          direction="column"
          gap="8px"
          flex="1"
          overflow="auto"
        >
          <Flex direction="column" width="100%">
            <StagingHeader manager={manager} />
            <List options={options} />
          </Flex>
          <Pagination
            modifier="full"
            data={paginatedKeys}
            onDataChange={setPaginatedData}
            startPage={1}
            textTags={getLocalizedPaginationStrings()}
            preserveSelectedPage
          />
        </Flex>
      )}
    </DropArea>
  );
};

export default StagingArea;
